import { useLocation } from "@reach/router"
import React from "react"
import { imageItemTransformer } from "../utils/transformers"
import Image from "./ImageOptim"
import Link from "./LinkWrapper"
import { cva, cx } from "class-variance-authority"
import { OpeningQuotationMark, ClosingQuotationMark } from "./QuotationMarks"

const testimonialClasses = cva(
  "py-1 border-none mb-0 text-gray-900 leading-normal [&_small:before]:hidden",
  {
    variants: {
      position: {
        left: "",
        center: "text-center w-[600px] max-w-full mx-auto",
      },
    },
    defaultVariants: {
      position: "left",
    },
  }
)

/* The closing quote is included in-line after text in this node. It has a
  negative margin equal to it’s width (35px) to ensure it is never
  orphaned. When text on the last line is within 35px of the right side of
  the container, the quote may get clipped. Setting padding prevents this
  from happening, with a side effect being that text will wrap sooner than
  it has to. */
const testimonialTextClasses = cva([
  "mb-0 relative font-bold text-lg leading-normal",
])

/*
 * HTML `data-*` attributes are not valid for the HTMLAttributes type. This
 * works around that limitation by creating a new type that allows any `data-*`
 * https://github.com/microsoft/TypeScript/issues/28960#issuecomment-903519759
 */

type DataAttributeKey = `data-${string}`

interface HTMLAttributes extends React.HTMLAttributes<HTMLAnchorElement> {
  [dataAttribute: DataAttributeKey]: any
}

export interface TestimonialProps {
  /**
   * The quote to display
   */
  quote: string
  /**
   * Who the quote is attributed to
   */
  attribution?: string
  /**
   * Optional image of the person being quoted. This is passed as the Kontent Image item data and will be transformed in the component
   */
  image?: any
  /**
   * Optional job title of the person being quoted
   */
  jobTitle?: string
  /**
   * The language the testimonial will be rendered in. If no language is provided, it will default to English-style quotation marks.
   */
  language?: string
  /**
   * Optional link to a destination relevant to the testimonial, such as a case study
   */
  linkTo?: string
  /**
   * The text to display for the link
   */
  linkLabel?: string
  /**
   * Optional attributes to apply to the link, typically for analytics
   */
  linkAttr?: HTMLAttributes
  /**
   * Whether to center the quote or align it to the left
   */
  position?: "left" | "center"
  /**
   * Optional class name
   */
  className?: string
}

/**
 * A testimonial component that displays a quote, attribution, and optional image
 */
const Testimonial = ({
  quote,
  attribution,
  image,
  jobTitle,
  language = "en",
  linkTo,
  linkLabel,
  linkAttr,
  position,
  className,
  ...attr
}: TestimonialProps) => {
  /* Determine if testimonial links to current page (we will exclude the link if so) */
  const location = useLocation()
  const { pathname } = location
  const linkIsCurrent = linkTo === pathname

  if (!quote) {
    return null
  }

  // This logic is here to help deal with spacing for the last word
  // and closing quotation mark when we have a sentence that is close
  // to the width of its container. There is an additional check for
  // Chinese and Japanese here - due to the nature of those languages,
  // we need to split on characters rather than the last word.
  const isAsianLanguage = language === "cn" || language === "jp" ? true : false
  const lastWord = isAsianLanguage ? quote.slice(-2) : quote.split(" ").pop()
  const quoteWithoutLastWord = isAsianLanguage
    ? quote.slice(0, -2)
    : quote.split(" ").slice(0, -1).join(" ")

  return (
    <div>
      <blockquote
        className={testimonialClasses({ position, className })}
        data-analytics-region="Testimonial"
        {...attr}
      >
        <p className={testimonialTextClasses()}>
          <span
            className={cx(`inline-block -mt-[27px]`, {
              "w-[28px] mr-[0.4em]": ["en", "it", "cn"].includes(language),
              "w-[28px] translate-y-[8px] mr-[0.4em]":
                language === "de" || language === "pl",
              "w-[24px] mr-[6px] translate-y-[7px]": [
                "es",
                "fr",
                "pt",
              ].includes(language),
              "w-[16px] mr-[4px]": language === "jp",
            })}
          >
            <OpeningQuotationMark language={language} />
          </span>
          {quoteWithoutLastWord}
          <span className="whitespace-nowrap">
            &nbsp;{lastWord}
            <span
              className={cx(`inline-block -mt-[27px]`, {
                "w-[28px] ml-[0.4em] -mb-[14px]": [
                  "pl",
                  "en",
                  "it",
                  "cn",
                ].includes(language),
                "w-[28px] translate-y-[4px] ml-[0.4em]": language === "de",
                "w-[24px] ml-[6px] translate-y-[7px]": [
                  "es",
                  "fr",
                  "pt",
                ].includes(language),
                "w-[16px] translate-y-[22px]": language === "jp",
              })}
            >
              <ClosingQuotationMark language={language} />
            </span>
          </span>
        </p>
        {attribution ? (
          <div className="mt-4">
            <small
              className={`text-sm leading-snug flex items-center text-gray-700 ${
                position === "center"
                  ? `justify-center ${image && `flex-col sm:flex-row`}`
                  : ``
              }`}
            >
              {image ? (
                <div
                  className={`rounded-full overflow-hidden w-12 ${
                    image && position === "center"
                      ? `flex justify-center mb-2 sm:mb-0 sm:mr-2`
                      : `mr-2`
                  }`}
                >
                  <Image
                    {...imageItemTransformer(image)}
                    cropWidth={48}
                    cropRatio={1}
                  />
                </div>
              ) : null}
              <div
                className={`${
                  image && position === "center"
                    ? `text-center sm:text-left`
                    : ``
                }`}
              >
                <strong>{attribution}</strong>
                {jobTitle ? (
                  <>
                    <br />
                    {jobTitle}
                  </>
                ) : null}
              </div>
            </small>
            {linkTo && linkLabel && !linkIsCurrent ? (
              <div className="mt-1 text-sm leading-snug">
                <Link
                  to={linkTo}
                  className={`text-action-link no-underline transition-colors duration-150 hover:text-action focus:outline-none focus:ring`}
                  {...linkAttr}
                >
                  {linkLabel}
                  <i
                    className="icon-chevron-right"
                    style={{ marginRight: `-14px` }}
                  ></i>
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </blockquote>
    </div>
  )
}

export default Testimonial
